// Catalogue editor · Mart: variants (sibling products: "Coke 290 ml / 1 L / 1.5 L") and value
// packs (a Mart combo of other Mart products, with the savings preview). Pure: no DOM, no Intl.
// Contract: docs/PHASE5-CONTRACT.md "Catalogue editor" + the "Catalogue management additions"
// comment in contract.d.ts (variantOf / variantLabel / mart.pack on ProductInput).

import { packSavings } from '@popcart/core/v1';
import { parsePesos } from './money.js';
import { peso } from '../../lib/format.js';

/** @typedef {import('@popcart/core/v1').AdminProduct} AdminProduct */
/** @typedef {import('@popcart/core/v1').AdminProductDetail} AdminProductDetail */
/** @typedef {import('@popcart/core/v1').ProductInput} ProductInput */
/** @typedef {import('@popcart/core/v1').PackPartView} PackPartView */

/**
 * AdminProduct with the Phase 5 additions (typed in contract comments only).
 * @typedef {AdminProduct & { variantOf?: string | null; variantLabel?: string | null; variantCount?: number }} MartAdminProduct
 */
/**
 * AdminProductDetail with the Phase 5 additions. Pack parts may arrive as `mart.pack`,
 * `pack.parts` or `packParts` (the contract names them only as "pack parts"); see packPartsOf.
 * @typedef {AdminProductDetail & {
 *   variantOf?: string | null; variantLabel?: string | null; variantCount?: number;
 *   variants?: MartAdminProduct[];
 *   mart?: { pack?: PackPartView[] | null } | null;
 *   pack?: { parts?: PackPartView[] } | PackPartView[] | null;
 *   packParts?: PackPartView[] | null;
 * }} MartAdminDetail
 */
/**
 * ProductInput with the Phase 5 additions.
 * @typedef {ProductInput & { variantOf?: string | null; variantLabel?: string | null; mart?: { pack?: { productId: string; qty: number }[] } }} MartProductInput
 */
/**
 * One part of a value pack in the form.
 * @typedef {object} PackDraft
 * @property {string} productId
 * @property {string} name
 * @property {string} qty       as typed
 * @property {number} priceCentavos  the part's own price (for the savings preview)
 */

export const MART_LIMITS = { variantLabel: 30, maxPack: 10, packQty: 20, barcode: 32, stockPieces: 100000 };

const CONTROL_RE = /[\u0000-\u001f\u007f]/;
/** @param {string} s */
const intOf = (s) => (/^\s*-?\d+\s*$/.test(s) ? Number(s.trim()) : NaN);

// ── Names ─────────────────────────────────────────────────────────────────────────────────

/** "Coke · 1 L" for lists; the plain name when it isn't a variant. @param {{ name: string; variantLabel?: string | null }} p */
export function listName(p) {
  return p.variantLabel ? `${p.name} · ${p.variantLabel}` : p.name;
}

/** Is this product a variant of another? @param {{ variantOf?: string | null }} p */
export function isVariant(p) {
  return !!p.variantOf;
}

/**
 * "Add a variant" is offered on a Mart base product: not on a variant (a variant can't have
 * variants), not on a value pack (its stock comes from its parts), not while archived.
 * @param {MartAdminDetail | null} p
 */
export function canAddVariant(p) {
  return !!p && p.line === 'mart' && !p.variantOf && !p.isCombo && p.status !== 'archived';
}

/**
 * The product's sibling variants (the base itself left out, whichever way the server lists it).
 * @param {MartAdminDetail | null} p
 * @returns {MartAdminProduct[]}
 */
export function variantsOf(p) {
  if (!p || !Array.isArray(p.variants)) return [];
  return p.variants.filter((v) => v.id !== p.id);
}

// ── Add a variant ─────────────────────────────────────────────────────────────────────────

/** @typedef {{ label: string; price: string; barcode: string; stock: string }} VariantDraft */

/** @returns {VariantDraft} */
export function emptyVariant() {
  return { label: '', price: '', barcode: '', stock: '' };
}

/**
 * Check the "Add a variant" form.
 * @param {VariantDraft} v
 * @param {{ takenLabels?: (string | null | undefined)[] }} [ctx] labels already used by the base and its variants
 * @returns {Record<string, string>} empty when valid; keys label | price | barcode | stock
 */
export function validateVariant(v, ctx = {}) {
  /** @type {Record<string, string>} */
  const e = {};
  const label = v.label.trim();
  if (!label) e.label = 'Name the variant, like 1 L or Sweet.';
  else if (label.length > MART_LIMITS.variantLabel) e.label = `Keep the label to ${MART_LIMITS.variantLabel} characters.`;
  else if (CONTROL_RE.test(label)) e.label = 'Remove the special characters from the label.';
  else if ((ctx.takenLabels ?? []).some((t) => t && t.trim().toLowerCase() === label.toLowerCase())) e.label = `“${label}” is already a variant of this product.`;

  const p = parsePesos(v.price);
  if (!p.ok) e.price = p.error;
  else if (p.centavos <= 0) e.price = 'The price must be more than ₱0.';

  const bc = v.barcode.trim();
  if (bc.length > MART_LIMITS.barcode) e.barcode = `Keep the barcode to ${MART_LIMITS.barcode} characters.`;
  else if (bc && !/^[A-Za-z0-9-]+$/.test(bc)) e.barcode = 'Barcodes are letters, numbers and dashes.';

  const s = v.stock.trim().replace(/,/g, '');
  if (s && !/^\d+$/.test(s)) e.stock = 'Enter a whole number of pieces.';
  else if (s && Number(s) > MART_LIMITS.stockPieces) e.stock = 'That is more than 100,000. Check the amount.';
  return e;
}

/**
 * The createProduct body for a new variant: a sibling that shares the base's name, category,
 * unit, shelf and description, with its own label, price, barcode and stock.
 * @param {MartAdminDetail} base @param {VariantDraft} v
 * @returns {MartProductInput}
 */
export function buildVariantInput(base, v) {
  const p = parsePesos(v.price);
  const s = v.stock.trim().replace(/,/g, '');
  return {
    name: base.name,
    line: 'mart',
    pricing: 'unit',
    priceCentavos: p.ok ? p.centavos : 0,
    unitLabel: base.unitLabel,
    description: base.description,
    categoryId: base.categoryId,
    source: base.source,
    sort: base.sort,
    barcode: v.barcode.trim() || null,
    shelf: base.shelf,
    visible: true,
    initialStock: s ? Number(s) : 0,
    variantOf: base.id,
    variantLabel: v.label.trim(),
  };
}

/** The variant label field on the main form. @param {string} label @param {{ required: boolean }} o */
export function variantLabelError(label, o) {
  const t = label.trim();
  if (!t) return o.required ? 'A variant needs a label, like 1 L or Sweet.' : null;
  if (t.length > MART_LIMITS.variantLabel) return `Keep the label to ${MART_LIMITS.variantLabel} characters.`;
  if (CONTROL_RE.test(t)) return 'Remove the special characters from the label.';
  return null;
}

// ── Value packs ───────────────────────────────────────────────────────────────────────────

/**
 * Pack parts from a product detail, wherever the server put them.
 * @param {MartAdminDetail} p
 * @returns {PackDraft[]}
 */
export function packPartsOf(p) {
  /** @type {PackPartView[]} */
  let parts = [];
  if (p.mart && Array.isArray(p.mart.pack)) parts = p.mart.pack;
  else if (Array.isArray(p.pack)) parts = p.pack;
  else if (p.pack && Array.isArray(p.pack.parts)) parts = p.pack.parts;
  else if (Array.isArray(p.packParts)) parts = p.packParts;
  return parts.map((x) => ({ productId: x.productId, name: x.name, qty: String(x.qty), priceCentavos: x.priceCentavos ?? 0 }));
}

/**
 * Products that can go into a pack: the shop's active Mart products, not packs themselves, not
 * the product being edited and not already in.
 * @param {MartAdminProduct[]} products @param {PackDraft[]} pack @param {string | null} selfId
 */
export function packCandidates(products, pack, selfId) {
  const inPack = new Set(pack.map((c) => c.productId));
  return products.filter((p) => p.line === 'mart' && p.status === 'active' && !p.isCombo && p.id !== selfId && !inPack.has(p.id));
}

/**
 * Add a product to the pack (or raise its quantity when it's already in).
 * @param {PackDraft[]} pack @param {MartAdminProduct} p
 * @returns {PackDraft[]}
 */
export function addPackPart(pack, p) {
  const at = pack.findIndex((c) => c.productId === p.id);
  if (at >= 0) return pack.map((c, i) => (i === at ? { ...c, qty: String((intOf(c.qty) || 0) + 1) } : c));
  return [...pack, { productId: p.id, name: listName(p), qty: '1', priceCentavos: p.priceCentavos }];
}

/**
 * Check the pack parts.
 * @param {PackDraft[]} pack @param {string | null} selfId
 * @returns {string | null}
 */
export function packError(pack, selfId) {
  if (!pack.length) return null;
  if (pack.length > MART_LIMITS.maxPack) return `Up to ${MART_LIMITS.maxPack} items in a pack.`;
  const ids = new Set();
  for (const c of pack) {
    if (selfId && c.productId === selfId) return 'A pack can’t contain itself.';
    if (ids.has(c.productId)) return `${c.name} is in the pack twice. Raise its quantity instead.`;
    ids.add(c.productId);
    const q = intOf(c.qty);
    if (!Number.isInteger(q) || q < 1 || q > MART_LIMITS.packQty) return `${c.name}: quantity is 1 to ${MART_LIMITS.packQty}.`;
  }
  if (pack.length === 1 && intOf(pack[0]?.qty ?? '') === 1) return 'A pack needs more than one item. Add another, or raise the quantity.';
  return null;
}

/** The wire parts. @param {PackDraft[]} pack */
export function packPayload(pack) {
  return pack.map((c) => ({ productId: c.productId, qty: intOf(c.qty) }));
}

/**
 * The savings preview ("Save ₱11") from the shared packSavings rule. null until the price and
 * every quantity can be read.
 * @param {string} price pesos as typed @param {PackDraft[]} pack
 * @returns {{ partsCentavos: number; packCentavos: number; savingsCentavos: number; text: string } | null}
 */
export function packPreview(price, pack) {
  if (!pack.length) return null;
  const p = parsePesos(price);
  if (!p.ok || p.centavos <= 0) return null;
  const parts = pack.map((c) => ({ priceCentavos: c.priceCentavos, qty: intOf(c.qty) }));
  if (parts.some((x) => !Number.isInteger(x.qty) || x.qty < 1)) return null;
  const partsCentavos = parts.reduce((t, x) => t + x.priceCentavos * x.qty, 0);
  const savingsCentavos = packSavings(p.centavos, parts);
  return {
    partsCentavos,
    packCentavos: p.centavos,
    savingsCentavos,
    text: savingsCentavos > 0
      ? `Customers see “Save ${peso(savingsCentavos)}”: the items cost ${peso(partsCentavos)} on their own.`
      : `No saving shown: the items cost ${peso(partsCentavos)} on their own, so the pack isn’t cheaper.`,
  };
}

/** "₱25 × 2" for a part row. @param {PackDraft} c */
export function partPriceLabel(c) {
  return `${peso(c.priceCentavos)} each`;
}

